import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import type { Tone } from './feedback';
import { Icon, type IconName } from './Icon';

const BADGE: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-fg ring-border-strong/50',
  info: 'bg-info-soft text-info ring-info/25',
  success: 'bg-success-soft text-success ring-success/25',
  warning: 'bg-warning-soft text-warning ring-warning/30',
  danger: 'bg-danger-soft text-danger ring-danger/25',
  brand: 'bg-brand-soft text-brand ring-brand/25',
};

export function Badge({ tone = 'neutral', icon, children, className }: { tone?: Tone; icon?: IconName; children: ReactNode; className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold tracking-tight whitespace-nowrap ring-1 ring-inset', BADGE[tone], className)}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

/** Colour conventions shared by every screen. */
export const SURVEY_STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  IN_REVIEW: 'brand',
  RETURNED: 'warning',
  CONFIRMED: 'success',
  COMMUNICATED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'neutral',
};

export const FINDING_STATUS_TONE: Record<string, Tone> = {
  OPEN: 'danger',
  COMMUNICATED: 'warning',
  ACTION_AGREED: 'info',
  RESOLVED: 'success',
  RISK_ACCEPTED: 'warning',
  CLOSED: 'neutral',
};

export const SEVERITY_TONE: Record<string, Tone> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
};

/** Days to follow-up: negative is overdue (red), ≤30 amber, otherwise neutral. */
export function followUpTone(days: number | null | undefined): Tone {
  if (days === null || days === undefined) return 'neutral';
  if (days < 0) return 'danger';
  if (days <= 30) return 'warning';
  return 'success';
}

export function Card({ className, children, interactive, ...rest }: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cx(
        'rounded-[var(--radius)] border border-border bg-surface shadow-card',
        interactive && 'transition-all duration-150 hover:border-border-strong hover:shadow-md active:bg-surface-2/60',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2.5 mt-6 flex items-center justify-between gap-2 first:mt-0">
      <h2 className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.08em] text-muted">
        <span aria-hidden="true" className="h-3.5 w-1 rounded-full bg-brand" />
        {children}
      </h2>
      {action}
    </div>
  );
}

export function KpiTile({
  label,
  value,
  tone = 'neutral',
  active,
  onClick,
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  active?: boolean;
  onClick?: () => void;
  hint?: string;
}) {
  const accent: Record<Tone, string> = {
    neutral: 'text-fg',
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    brand: 'text-brand',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick ? !!active : undefined}
      title={hint}
      className={cx(
        'flex min-h-24 flex-col justify-between rounded-[var(--radius)] border bg-surface p-4 text-start shadow-card transition-all duration-150',
        active ? 'border-brand ring-2 ring-brand/25' : 'border-border',
        onClick && 'hover:border-border-strong hover:shadow-md',
      )}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">{label}</span>
      <span className={cx('font-display text-3xl font-bold tracking-tight tabular-nums', accent[tone])}>{value}</span>
    </Tag>
  );
}

export function ProgressBar({ value, max = 100, label, tone = 'brand' }: { value: number; max?: number; label?: string; tone?: 'brand' | 'success' }) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label} className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
      <div className={cx('h-full rounded-full transition-[width] duration-300', tone === 'success' ? 'bg-success' : 'bg-brand')} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function ProgressRing({ value, max, size = 44 }: { value: number; max: number; size?: number }) {
  const pct = max <= 0 ? 0 : value / max;
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const done = max > 0 && value >= max;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-2)" strokeWidth="5" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={done ? 'var(--success)' : 'var(--brand)'}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={`${c * pct} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {done ? (
        <path d={`M${size * 0.34} ${size * 0.5} l${size * 0.11} ${size * 0.11} l${size * 0.2} -${size * 0.22}`} stroke="var(--success)" strokeWidth="3" fill="none" strokeLinecap="round" />
      ) : (
        <text x="50%" y="54%" textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.26} fontWeight="700" fill="var(--text)">
          {Math.round(pct * 100)}
        </text>
      )}
    </svg>
  );
}

export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="flex items-center gap-2">
      {steps.map((s, i) => (
        <li key={s} className="flex flex-1 items-center gap-2">
          <span
            className={cx(
              'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold',
              i < current ? 'bg-success text-white' : i === current ? 'bg-brand text-brand-contrast' : 'bg-surface-2 text-muted',
            )}
          >
            {i < current ? <Icon name="check" size={16} strokeWidth={3} /> : i + 1}
          </span>
          <span className={cx('hidden text-sm sm:inline', i === current ? 'font-semibold' : 'text-muted')}>{s}</span>
          {i < steps.length - 1 && <span className="h-0.5 flex-1 rounded bg-border" />}
        </li>
      ))}
    </ol>
  );
}

const AVATAR_TONES = ['#181816', '#253342', '#6E1A22', '#174636', '#332C4A', '#4E3829'] as const;

export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const initials = name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % AVATAR_TONES.length;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-lg font-display font-bold tracking-tight text-white ring-1 ring-inset ring-white/15"
      style={{ width: size, height: size, background: AVATAR_TONES[h], fontSize: size * 0.36 }}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { value: T; label: ReactNode; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="scrollbar-thin flex gap-1 overflow-x-auto border-b border-border">
      {tabs.map((t) => {
        const on = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.value)}
            className={cx(
              '-mb-px inline-flex min-h-11 items-center gap-2 whitespace-nowrap border-b-2 px-3.5 text-sm font-semibold tracking-tight transition-colors',
              on ? 'border-brand text-fg' : 'border-transparent text-muted hover:text-fg',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span className={cx('rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums', on ? 'bg-brand-soft text-brand' : 'bg-surface-2 text-muted')}>
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
